import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';

import { ColumnChooser } from './ColumnChooser';
import type { ColumnDef, SortState } from './types';
import {
  type ColumnLayoutPersistence,
  useColumnLayout,
} from './useColumnLayout';

const ALIGN_CLASS = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
} as const;

const HEAD_CLASS =
  'relative h-auto border-b border-gray-200 bg-gray-50 px-2 py-1.5 text-left text-[11px] font-semibold tracking-wide text-gray-500 uppercase select-none';
const CELL_CLASS =
  'border-b border-gray-100 px-2 py-0.5 align-middle overflow-hidden text-ellipsis whitespace-nowrap';

export type DataTableProps<Row> = {
  rows: Row[];
  columns: ColumnDef<Row>[];
  /** Stable row identity — used as the React key. */
  getRowId: (row: Row) => string;
  /**
   * localStorage key for persisted column widths and order. Omit to make the
   * layout session-only.
   */
  storageKey: string;
  /**
   * `auto` (default) persists every layout change. `manual` keeps changes
   * session-only and shows a "Save view" button that persists them.
   */
  layoutPersistence?: ColumnLayoutPersistence;
  /** Enables drag-to-reorder on column headers. */
  reorderable?: boolean;
  /** Renders a show/hide column chooser above the table. */
  columnChooser?: boolean;
  /** Extra content rendered in the toolbar beside the column chooser. */
  toolbar?: ReactNode;
  emptyMessage?: string;
  className?: string;
  /** Extra classes merged onto every header cell, overriding the defaults. */
  headerClassName?: string;
  /** Stretches columns proportionally so the table spans its container. */
  fillWidth?: boolean;
  rowClassName?: (row: Row) => string | undefined;
  onRowContextMenu?: (row: Row) => void;
  /** Initial sort, applied until the user clicks a sortable header. */
  defaultSort?: SortState;
  /** Called with rows in display (sorted) order, e.g. for shift-click ranges. */
  onSortedRowsChange?: (rows: Row[]) => void;
};

const isBlank = (v: unknown) => v == null || v === '';

/**
 * Compares two non-blank cell values.
 *
 * Straw and other grid identifiers arrive as numbers from some endpoints and
 * numeric strings from others, so numeric-looking values are compared as
 * numbers — otherwise straw 10 sorts between 1 and 2 (WI 37941 #12).
 */
function compareValues(a: unknown, b: unknown): number {
  const aNum = typeof a === 'number' ? a : Number(a);
  const bNum = typeof b === 'number' ? b : Number(b);
  if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) return aNum - bNum;

  return String(a).localeCompare(String(b), undefined, { numeric: true });
}

/**
 * A dense, dependency-free data table with resizable, reorderable and
 * persisted columns.
 *
 * This deliberately does NOT use ag-grid. It exists for cases where many
 * tables are mounted at once (e.g. inside expanded detail rows) and the
 * per-instance cost of a full grid is too high. Columns are described by
 * `ColumnDef`s so the header, body and width model stay in sync from one
 * source of truth.
 *
 * Pass a referentially stable `columns` array (module constant or `useMemo`)
 * — a new array on every render defeats the memoised layout.
 */
export function DataTable<Row>({
  rows,
  columns,
  getRowId,
  storageKey,
  layoutPersistence = 'auto',
  reorderable = false,
  columnChooser = false,
  toolbar,
  emptyMessage,
  className,
  headerClassName,
  fillWidth = false,
  rowClassName,
  onRowContextMenu,
  defaultSort,
  onSortedRowsChange,
}: DataTableProps<Row>) {
  const {
    orderedColumns,
    visibleColumns,
    hiddenKeys,
    widths,
    totalWidth,
    startResize,
    moveColumn,
    toggleColumn,
    resetLayout,
    saveLayout,
    isDirty,
  } = useColumnLayout(storageKey, columns, {
    persistence: layoutPersistence,
  });
  const manualSave = layoutPersistence === 'manual';

  const [dragKey, setDragKey] = useState<string | null>(null);
  const [overKey, setOverKey] = useState<string | null>(null);
  const [sort, setSort] = useState<SortState | null>(defaultSort ?? null);

  /** asc -> desc -> unsorted, so the user can always get back to source order. */
  const toggleSort = useCallback((key: string) => {
    setSort((current) => {
      if (current?.key !== key) return { key, direction: 'asc' };
      if (current.direction === 'asc') return { key, direction: 'desc' };
      return null;
    });
  }, []);

  const sortedRows = useMemo(() => {
    if (!sort) return rows;
    const col = columns.find((c) => c.key === sort.key);
    if (!col?.sortable) return rows;

    const valueOf = (row: Row) =>
      col.sortValue
        ? col.sortValue(row)
        : (row as Record<string, unknown>)[col.key];

    const factor = sort.direction === 'asc' ? 1 : -1;
    // Sorting must not mutate the caller's array — grids pass their state in.
    return [...rows].sort((a, b) => {
      const aVal = valueOf(a);
      const bVal = valueOf(b);
      // Blanks sink to the bottom in both directions: an unset straw is never
      // what the user is scanning for, so it shouldn't lead the descending view.
      if (isBlank(aVal) || isBlank(bVal)) {
        return isBlank(aVal) && isBlank(bVal) ? 0 : isBlank(aVal) ? 1 : -1;
      }
      return compareValues(aVal, bVal) * factor;
    });
  }, [rows, columns, sort]);

  useEffect(() => {
    onSortedRowsChange?.(sortedRows);
  }, [onSortedRowsChange, sortedRows]);

  const canReorder = useCallback(
    (col: ColumnDef<Row>) => reorderable && col.reorderable !== false,
    [reorderable],
  );

  const endDrag = useCallback(() => {
    setDragKey(null);
    setOverKey(null);
  }, []);

  return (
    <div className="flex w-full flex-col gap-1">
      {(columnChooser || toolbar || manualSave) && (
        <div className="flex items-center gap-2">
          {columnChooser && (
            <ColumnChooser
              columns={orderedColumns}
              hiddenKeys={hiddenKeys}
              onToggle={toggleColumn}
              onReset={resetLayout}
            />
          )}
          {manualSave && (
            <>
              <Button
                variant="ghost"
                size="xs"
                onClick={saveLayout}
                disabled={!isDirty}
                title="Save column order and widths"
              >
                <i className="pi pi-save text-xs" />
                Save view
              </Button>
              {!columnChooser && (
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={resetLayout}
                  title="Restore the default column order and widths"
                >
                  <i className="pi pi-refresh text-xs" />
                  Reset
                </Button>
              )}
            </>
          )}
          {toolbar}
        </div>
      )}
      <div className="w-full overflow-x-auto rounded border border-gray-200">
        <Table
          className={cn('w-auto border-collapse text-xs', className)}
          style={{
            tableLayout: 'fixed',
            width: totalWidth,
            minWidth: fillWidth ? '100%' : undefined,
          }}
        >
          <colgroup>
            {visibleColumns.map((col) => (
              <col
                key={col.key}
                style={{ width: widths[col.key] ?? col.width }}
              />
            ))}
          </colgroup>
          <TableHeader className="bg-gray-100 text-gray-600">
            <TableRow className="hover:bg-transparent">
              {visibleColumns.map((col) => (
                <TableHead
                  key={col.key}
                  title={col.headerTitle}
                  aria-sort={
                    col.sortable
                      ? sort?.key === col.key
                        ? sort.direction === 'asc'
                          ? 'ascending'
                          : 'descending'
                        : 'none'
                      : undefined
                  }
                  onClick={col.sortable ? () => toggleSort(col.key) : undefined}
                  draggable={canReorder(col)}
                  onDragStart={
                    canReorder(col) ? () => setDragKey(col.key) : undefined
                  }
                  onDragOver={
                    canReorder(col)
                      ? (e) => {
                          if (!dragKey || dragKey === col.key) return;
                          // Required for the drop event to fire at all.
                          e.preventDefault();
                          setOverKey(col.key);
                        }
                      : undefined
                  }
                  onDrop={
                    canReorder(col)
                      ? (e) => {
                          e.preventDefault();
                          if (dragKey) moveColumn(dragKey, col.key);
                          endDrag();
                        }
                      : undefined
                  }
                  onDragEnd={canReorder(col) ? endDrag : undefined}
                  className={cn(
                    HEAD_CLASS,
                    col.align && ALIGN_CLASS[col.align],
                    canReorder(col) && 'cursor-grab',
                    col.sortable && 'cursor-pointer hover:text-gray-700',
                    dragKey === col.key && 'opacity-50',
                    overKey === col.key && 'bg-blue-100',
                    headerClassName,
                  )}
                >
                  {col.header}
                  {col.sortable && sort?.key === col.key && (
                    <span aria-hidden className="ml-1">
                      {sort.direction === 'asc' ? '\u2191' : '\u2193'}
                    </span>
                  )}
                  <span
                    role="separator"
                    aria-orientation="vertical"
                    aria-label={`Resize column`}
                    draggable={false}
                    onDragStart={(e) => e.preventDefault()}
                    /* Without this, grabbing the resize handle also sorts. */
                    onClick={(e) => e.stopPropagation()}
                    onPointerDown={(e) => {
                      e.stopPropagation();
                      startResize(col.key, e);
                    }}
                    className="absolute top-0 right-0 h-full w-1 cursor-col-resize select-none hover:bg-blue-300"
                  />
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedRows.length === 0 && emptyMessage ? (
              <TableRow>
                <TableCell
                  colSpan={visibleColumns.length}
                  className={cn(CELL_CLASS, 'py-3 text-center text-gray-500')}
                >
                  {emptyMessage}
                </TableCell>
              </TableRow>
            ) : (
              sortedRows.map((row) => (
                <TableRow
                  key={getRowId(row)}
                  onContextMenu={
                    onRowContextMenu ? () => onRowContextMenu(row) : undefined
                  }
                  className={cn(
                    'border-0 odd:bg-white even:bg-gray-50',
                    rowClassName?.(row),
                  )}
                >
                  {visibleColumns.map((col) => (
                    <TableCell
                      key={col.key}
                      style={col.cellStyle?.(row)}
                      className={cn(
                        CELL_CLASS,
                        // Editable cells tighten their padding so the input's
                        // box (see EditableCell) has room without the row
                        // growing. The v2 `editable-cell` pencil glyph is
                        // deliberately not used here — the visible box restored
                        // from v1 is the affordance, and the glyph would sit on
                        // top of the input's top-right corner.
                        col.editable && 'px-1 py-0',
                        col.align && ALIGN_CLASS[col.align],
                        col.cellClassName,
                      )}
                    >
                      {col.render(row)}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
